import { LanguageType } from '@lifeserver/shared';

import { compareDates, isTodayBetweenDates } from '../utils/dates';
import { handleError } from '../utils/utils.ts';

import { PackType } from '../types/enums/packtype.enum';

import { Pack, PackListItem } from '../types/packTypes';

const SKU_NAME_PREFIX = 'com.tocaboca.tocalifeworld.';

export function removeStandalonePacks<T extends Pack | PackListItem>(
    packs: T[],
): T[] {
    return packs.filter((pack) => pack.type !== PackType.STANDALONE);
}

export function filterPacksBySearchString<T extends Pack | PackListItem>(
    packs: T[],
    searchString: string,
    language: LanguageType,
): T[] {
    if (!searchString) {
        return packs;
    }

    const loweredSearchString = searchString.toLowerCase();
    return packs.filter(
        (pack) =>
            pack.packId?.toLowerCase()?.includes(loweredSearchString) ||
            pack.name?.toLowerCase()?.includes(loweredSearchString) ||
            (pack.displayName as Record<string, string | undefined>)?.[language]
                ?.toLowerCase()
                ?.includes(loweredSearchString),
    );
}

export function filterPacksByStatus<T extends Pack | PackListItem>(
    packs: T[],
    status: string,
): T[] {
    if (status === 'all') {
        return packs;
    }

    return packs.filter((pack) => {
        const isActive = isTodayBetweenDates(
            pack.activeFromDate,
            pack.activeToDate,
        );
        return status === 'active' ? isActive : !isActive;
    });
}

export function filterPacksByType<T extends Pack | PackListItem>(
    packs: T[],
    filterType: string,
): T[] {
    if (filterType === 'all') {
        return packs;
    }

    return packs.filter((pack) => pack.type === filterType);
}

export type SortByType = 'name' | 'packId' | 'activeFromDate' | 'activeToDate';

function createComparators<T extends Pack | PackListItem>(): Record<
    SortByType,
    (a: T, b: T) => number
> {
    return {
        name: (a: T, b: T) =>
            a.name.localeCompare(b.name, undefined, {
                sensitivity: 'base',
            }),
        packId: (a: T, b: T) => a.packId.localeCompare(b.packId),
        activeFromDate: (a: T, b: T) =>
            compareDates(a.activeFromDate, b.activeFromDate),
        activeToDate: (a: T, b: T) =>
            compareDates(a.activeToDate, b.activeToDate),
    };
}

// Export comparators for tests and external use
export const comparators = createComparators<Pack>();

export function sortPacks<T extends Pack | PackListItem>(
    packs: T[],
    sortBy: SortByType | undefined,
    isSortAscend: boolean,
): T[] {
    if (!sortBy) {
        return packs;
    }

    const comparators = createComparators<T>();
    const comparator = comparators[sortBy];

    const sorted = packs.slice().sort(comparator);

    if (isSortAscend) {
        return sorted;
    }
    return sorted.toReversed();
}

export const hasSkuNamePrefix = (text: string): boolean => {
    return text.startsWith(SKU_NAME_PREFIX);
};

export const removeSkuNamePrefix = (text: string): string => {
    return text.split(SKU_NAME_PREFIX)[1] ?? text;
};

export const addSkuNamePrefix = (text: string): string => {
    return SKU_NAME_PREFIX + text;
};

export function getNameSuggestionFromId(packId: string): string {
    return packId.startsWith('pack.') ? packId.slice(5) : packId;
}

export function isPackBundle(pack: Pack): boolean {
    return [PackType.MULTIPACK, PackType.PACK, PackType.STANDALONE].includes(
        pack.type,
    );
}

export function calculateTotalWorth(
    nonBundlePacks: Pack[],
    selectedIds: string[],
): number {
    return nonBundlePacks
        .filter((x) => selectedIds.includes(x.packId))
        .reduce((acc, val) => acc + val.worth, 0);
}

export const extractProductIdsFromPacks = (
    packIds: string[],
    allPacks: Pack[],
): string[] => {
    return packIds.flatMap((packId) => {
        const pack = allPacks.find((p) => p.packId === packId);
        const productId = pack?.priceLevels?.[0]?.productId;

        if (!productId) {
            const errorMessage = `Couldn't find product id for pack ${packId}`;
            handleError(errorMessage, new Error(errorMessage));
            return [];
        }

        return [productId];
    });
};
